import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { fetchSalesPage, salesError } from '@/lib/salesOverview';
import type { SaleWithCustomer } from './useSales';
import type { DashboardRange } from './useDashboardRange';
import { operationsRpc } from '@/lib/operations';
export const DASHBOARD_KEY = 'dashboard_kpis';
export interface DashboardKPIs {
  organization_id: string;
  organization_name: string;
  sales_this_month: number;
  collected_this_month: number;
  refunds_this_month: number;
  total_receivable: number;
  gross_profit_this_month?: number;
  total_cash_balance?: number;
  total_stock_on_hand: number;
  total_stock_reserved: number;
  low_stock_count: number;
  return_review_count: number;
  as_of: string;
  overview_contract_version: number;
  purchase_value?: number;
  supplier_payments?: number;
}
export function useDashboardKPIs(range?: DashboardRange) {
  const { organization, profile, access, can } = useAuth();
  return useQuery({
    queryKey: [DASHBOARD_KEY, organization?.id, profile?.id, access?.access_version, range],
    enabled: !!organization && can('dashboard.view'),
    staleTime: 15000,
    refetchInterval: 60000,
    queryFn: async () => {
      if (range) {
        try {
          const summary = await operationsRpc<{
            current_position: DashboardKPIs;
            period: {
              invoice_sales: number;
              collections: number;
              refunds: number;
              invoice_profit?: number;
              purchase_value?: number;
              supplier_payments?: number;
            };
            generated_at: string;
          }>('get_dashboard_summary_v1', { p_filters: range });
          return {
            ...summary.current_position,
            sales_this_month: Number(summary.period.invoice_sales),
            collected_this_month: Number(summary.period.collections),
            refunds_this_month: Number(summary.period.refunds),
            gross_profit_this_month: summary.period.invoice_profit,
            purchase_value: summary.period.purchase_value,
            supplier_payments: summary.period.supplier_payments,
            as_of: summary.generated_at,
          };
        } catch (e) {
          if ((e as { code?: string }).code !== 'MIGRATION_REQUIRED') throw e;
        }
      }
      const { data, error } = await supabase.rpc('get_dashboard_kpis');
      if (error) throw new Error(salesError(error));
      if (!data || data.overview_contract_version !== 1)
        throw new Error(
          'The Sales Overview database migration is required before this dashboard version can be used.',
        );
      if (range) {
        const a = await operationsRpc<{
          totals: { sales: number; receipts: number; refunds: number; profit?: number };
        }>('get_sales_overview_analytics', { p_filters: range });
        return {
          ...data,
          sales_this_month: a.totals.sales,
          collected_this_month: a.totals.receipts,
          refunds_this_month: a.totals.refunds,
          gross_profit_this_month: a.totals.profit,
        } as DashboardKPIs;
      }
      return data as DashboardKPIs;
    },
  });
}
export function useRecentSales(range?: DashboardRange) {
  const { organization, profile, access, can } = useAuth();
  return useQuery({
    queryKey: ['recent_sales', organization?.id, profile?.id, access?.access_version, range],
    enabled: !!organization && can('sales.view'),
    staleTime: 15000,
    refetchInterval: 60000,
    queryFn: async () => {
      const result = await fetchSalesPage<SaleWithCustomer>({ ...range, limit: 10 });
      return result.rows;
    },
  });
}
